/**
 * Naming a workout day of a trainee's program.
 *
 * A program day is one row per WEEK on the server, so its name and its translations are written
 * through one endpoint that renames every week's copy at once — not through the generic
 * translation editor, which edits a single row. Reading the current translations still goes
 * through `TranslationService` (any one week's row holds the same text).
 */
import { adminProgramControllerRenameWorkoutDay } from '../api/generated';
import type { MobileMiniWorkout, RenameWorkoutDayResponse } from '../api/generated';

/** Same limit as the server's WorkoutDayNameService.MAX_NAME_LENGTH. */
export const MAX_WORKOUT_NAME_LENGTH = 60;

export interface RenameWorkoutDayInput {
    programId: number;
    groupUuid: string;
    nameHeb: string;
    /** locale code → name. A locale left out is CLEARED on the server. */
    translations: Record<string, string>;
}

type AxiosShapedError = Error & { response?: { data?: unknown; status?: number } };

// Callers read `e.response?.data?.errorCode` (getErrorMessage), as they did on axios.
const asAxiosError = (error: unknown, status?: number): AxiosShapedError => {
    const body = error as { errorMessage?: string; message?: string } | undefined;
    const err: AxiosShapedError = new Error(body?.errorMessage || body?.message || 'Request failed');
    err.response = { data: error, status };
    return err;
};

export const renameWorkoutDay = async (input: RenameWorkoutDayInput): Promise<RenameWorkoutDayResponse> => {
    const translations: Record<string, string> = {};
    Object.entries(input.translations).forEach(([locale, value]) => {
        if (value?.trim()) translations[locale] = value.trim();
    });
    const { data, error, response } = await adminProgramControllerRenameWorkoutDay({
        body: {
            programId: input.programId,
            groupUuid: input.groupUuid,
            nameHeb: input.nameHeb.trim(),
            translations,
        },
    });
    if (error) throw asAxiosError(error, response?.status);
    return data as RenameWorkoutDayResponse;
};

/**
 * The label a coach sees on a day tab: the name they chose, else the position. Mirrors the trainee
 * app, so the coach never sees a stored placeholder ("אימון B") the trainee does not.
 */
export const workoutDayLabel = (workout: Pick<MobileMiniWorkout, 'customName' | 'nameHeb'> | undefined, position: number): string =>
    workout?.customName && workout.nameHeb?.trim() ? workout.nameHeb.trim() : `אימון ${position}`;
